import { reportApiError } from '@/datadog/report-api-error';
import { isRequestCancelled } from '@/datadog/is-request-cancelled';
import { readErrorDetails } from '@/datadog/read-error-details';
import { DEFAULT_REVALIDATE_TIME } from '../constants';
import { getHeaders } from '../headers';
import type { FetchApi } from '../types';
import { ERROR_STATUS_CODES_TO_IGNORE } from './constants';

export const fetchApi = async ({
  method = 'GET',
  url,
  body,
  next: { revalidate, tags } = {},
  signal,
}: FetchApi) => {
  const { headers } = getHeaders();
  if (!headers) {
    return Response.json(
      {
        error: {
          status: 401,
          statusText: 'Ej behörig',
        },
        url,
      },
      { status: 401, statusText: 'Ej behörig' }
    );
  }
  const isBodyStringified = !!body && typeof body === 'string';

  const requestUrl =
    typeof url === 'string' ? url : url instanceof URL ? url.href : url.url;
  let response: Response;
  try {
    response = await fetch(url, {
      method: method.toUpperCase(),
      next: {
        revalidate: revalidate ?? DEFAULT_REVALIDATE_TIME,
        tags,
      },
      signal,
      headers: { ...headers },
      ...(!!body && {
        body: isBodyStringified ? body : JSON.stringify(body),
      }),
    });
  } catch (error) {
    const requestSignal =
      signal ?? (url instanceof Request ? url.signal : undefined);
    if (!isRequestCancelled(error, requestSignal)) {
      reportApiError({
        source: 'legacy-api',
        url: requestUrl,
        method,
        status: 0,
      });
    }
    throw error;
  }

  if (!response.ok && !ERROR_STATUS_CODES_TO_IGNORE.has(response.status)) {
    const details: unknown =
      response.status === 400 && typeof window !== 'undefined'
        ? await readErrorDetails(response)
        : undefined;
    reportApiError({
      source: 'legacy-api',
      url: requestUrl,
      method,
      status: response.status,
      details,
    });
  }

  return response;
};

type CustomErrorMessage = {
  errorMessage?: string;
};

async function api<T>(props: FetchApi & CustomErrorMessage) {
  const response = await fetchApi(props);

  if (!response.ok) {
    throw {
      status: response.status,
      message: props.errorMessage ?? response.statusText,
      url: response.url,
      errorData: await response.json().catch(() => undefined),
    };
  }
  if (response.status === 204) {
    return undefined as T;
  }

  const json = (await response.json()) as T;

  return json;
}

export function getApi<T>(
  props: Omit<FetchApi, 'method'> & CustomErrorMessage
) {
  return api<T>({ ...props, method: 'GET' });
}

export function postApi<T>(
  props: Omit<FetchApi, 'method'> & CustomErrorMessage
) {
  return api<T>({ ...props, method: 'POST' });
}

export function patchApi<T>(
  props: Omit<FetchApi, 'method'> & CustomErrorMessage
) {
  return api<T>({ ...props, method: 'PATCH' });
}

export function deleteApi<T>(
  props: Omit<FetchApi, 'method'> & CustomErrorMessage
) {
  return api<T>({ ...props, method: 'DELETE' });
}
